/**
 * The three app-level guard notices — new version, session needs refresh,
 * signing out for inactivity — as toasts. The guards in
 * `components/providers/` decide WHEN to raise them; this module owns WHAT
 * they say and look like, so `/dev/toasts` renders the real thing instead of
 * a copy that can drift.
 *
 * All three are persistent (stay until handled), carry a stable id so a
 * re-raise updates in place, and report telemetry under their own surface name
 * (`version-guard`, `session-guard`, `idle-guard`).
 *
 * The session and idle notices end the user's session (re-authenticate, or a
 * forced sign-out), so their text is an assertive alert rather than relying on
 * the Toaster's polite live region — see `UrgentNotice`.
 */

import type { ReactNode } from 'react'

import { hidePersistentToast, showPersistentToast } from '@/lib/persistent-toast'

export const GUARD_TOAST_ID = {
  version: 'guard-version',
  session: 'guard-session',
  idle: 'guard-idle',
} as const

export type GuardToastId = (typeof GUARD_TOAST_ID)[keyof typeof GUARD_TOAST_ID]

/**
 * The guard notices render in their own Toaster (see `CrucibleToaster`), not
 * in the main stack. The main stack shows at most four toasts and Compli can
 * hold four job toasts open at once, so a guard raised before them would be
 * pushed out of view — the idle sign-out could then happen with "Stay signed
 * in" unreachable. The guard Toaster holds only these three, so each is always
 * visible.
 */
export const GUARD_TOASTER_ID = 'guards'

/** Room for every guard notice at once. */
export const GUARD_TOASTER_VISIBLE = Object.keys(GUARD_TOAST_ID).length

/**
 * Description for a notice that signs the user out or makes them sign in
 * again. Sonner's region is `aria-live="polite"`, which a screen reader may
 * hold back behind whatever it is reading; this is announced at once, when the
 * toast appears. A live value inside (the idle countdown) belongs in
 * `<Countdown>`, so updating it in place is not re-announced every second.
 */
function UrgentNotice({ children }: { children: ReactNode }) {
  return (
    <span role="alert" aria-live="assertive" aria-atomic="true">
      {children}
    </span>
  )
}

/** A ticking value inside an `UrgentNotice`: shown, read on browse, never announced. */
function Countdown({ children }: { children: ReactNode }) {
  return (
    <span role="timer" aria-live="off" className="font-mono tabular-nums">
      {children}
    </span>
  )
}

/** Remove a guard toast because the guard's own state says it should be gone. */
export function hideGuardToast(id: GuardToastId): void {
  hidePersistentToast(id)
}

interface VersionToastOptions {
  /** Hash of the build the server is on; shown next to the running build. */
  serverHash?: string | null
  onReload: () => void
  /** "Later" and the × both land here. */
  onLater: () => void
}

export function showVersionToast({ serverHash, onReload, onLater }: VersionToastOptions) {
  return showPersistentToast(
    'info',
    'A new version of Crucible is ready',
    {
      id: GUARD_TOAST_ID.version,
      toasterId: GUARD_TOASTER_ID,
      description: (
        <>
          Reload to pick up the latest changes. Your current conversation is safe — it&rsquo;s
          saved on the server.
          {serverHash ? (
            <span className="mt-1 block font-mono text-caption">
              {process.env.NEXT_PUBLIC_BUILD_HASH ?? 'dev'} → {serverHash}
            </span>
          ) : null}
        </>
      ),
      action: { label: 'Reload', onClick: onReload },
      cancel: { label: 'Later', onClick: onLater },
      onDismiss: onLater,
    },
    {
      surface: 'version-guard',
      kind: 'action-required',
      props: { reason: 'bundle_stale', ...(serverHash ? { serverHash } : {}) },
    },
  )
}

interface SessionToastOptions {
  onReauth: () => void
  /** "Later" and the × both land here. */
  onLater: () => void
}

export function showSessionToast({ onReauth, onLater }: SessionToastOptions) {
  return showPersistentToast(
    'warning',
    'Session needs refresh',
    {
      id: GUARD_TOAST_ID.session,
      toasterId: GUARD_TOASTER_ID,
      description: (
        <UrgentNotice>
          Your login token has expired. Re-authenticate to continue — your conversation is saved.
        </UrgentNotice>
      ),
      action: { label: 'Re-authenticate', onClick: onReauth },
      cancel: { label: 'Later', onClick: onLater },
      onDismiss: onLater,
    },
    { surface: 'session-guard', kind: 'action-required', props: { reason: 'token_lost' } },
  )
}

interface IdleToastOptions {
  secondsLeft: number
  onStay: () => void
  /** The × — closes the notice only; the countdown carries on. */
  onClose: () => void
}

/** "2:00" above a minute, "45s" below — same text the old card showed. */
export function formatIdleCountdown(secondsLeft: number): string {
  const mins = Math.floor(secondsLeft / 60)
  const secs = secondsLeft % 60
  return mins > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : `${secondsLeft}s`
}

/** Raise, or tick in place, the inactivity countdown. */
export function showIdleToast({ secondsLeft, onStay, onClose }: IdleToastOptions) {
  return showPersistentToast(
    'warning',
    'Signing out due to inactivity',
    {
      id: GUARD_TOAST_ID.idle,
      toasterId: GUARD_TOASTER_ID,
      description: (
        <UrgentNotice>
          You&rsquo;ll be signed out in <Countdown>{formatIdleCountdown(secondsLeft)}</Countdown>.
          Your work is saved.
        </UrgentNotice>
      ),
      action: { label: 'Stay signed in', onClick: onStay },
      onDismiss: onClose,
    },
    { surface: 'idle-guard', kind: 'warning', props: { reason: 'inactivity_pending' } },
  )
}
